/**
 *
 * this context allows us to get confirmation from the user when they try to
 * navigate away from the current page.
 *
 * normal <a> tags, closing the current tab, and react router's link tag are
 * handled differently, noted below
 *
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Prompt } from 'react-router-dom';

type ReactRouterPromptHandler = ((location: Location) => boolean) | null;

type Value = {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (newHasUnsavedChanges: boolean) => void;
  setReactRouterPromptHandler: (fn: any) => void;
  reactRouterPromptHandler: ReactRouterPromptHandler;
};
const LEAVE_CONFIRMATION_COPY =
  'You have unsaved changes. Are you sure you want to leave?';

const initialState = {
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => null,
  setReactRouterPromptHandler: () => null,
  reactRouterPromptHandler: () => false,
};

const UnsavedChangesWarningContext = createContext<Value>(initialState);

export const useUnsavedChangesWarning = () =>
  useContext(UnsavedChangesWarningContext);

export const UnsavedChangesWarningProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [reactRouterPromptHandler, setHandler] = useState<
    ReactRouterPromptHandler
  >(null);

  const setReactRouterPromptHandler = useCallback(fn => {
    setHandler(() => fn);
  }, []);

  const value = {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    setReactRouterPromptHandler,
    reactRouterPromptHandler,
  };

  // this handles navigating away from the current page via react router
  // we have an optional way to determine if we should show the prompt if you
  // set the `reactRouterPromptHandler` function.
  // you would want to use this if your page utilizes different URL paths, like:
  // /dashboard/episode/new -- default page to create a new episode
  // /dashboard/episode/new/library -- same page, but now we're showing the library list
  // in this case if we navigate between the paths above, the default behaviour
  // would show a prompt if there are unsaved changes. we can override that behaviour
  // with this custom `reactRouterPromptHandler` function where returning `true`
  // _will not_ prompt the user and returning a string _will_ prompt the user
  function shouldPrompt(location: Location): string | boolean {
    let showConfirmation = true;
    if (reactRouterPromptHandler !== null) {
      showConfirmation = reactRouterPromptHandler(location);
    }
    return showConfirmation ? LEAVE_CONFIRMATION_COPY : true;
  }

  // this `beforeunload` handles navigating away from the current page via
  // clicking an <a> tag, refreshing, or closing the tab/window
  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault(); // shows native prompt for firefox
        e.returnValue = LEAVE_CONFIRMATION_COPY; // shows native prompt for webkit-derived browsers
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [hasUnsavedChanges]);

  return (
    <UnsavedChangesWarningContext.Provider value={value}>
      {/* <Prompt> handles navigating away from the current page via react router */}
      {/*
        // @ts-ignore */}
      <Prompt when={hasUnsavedChanges} message={shouldPrompt} />
      {children}
    </UnsavedChangesWarningContext.Provider>
  );
};
